/**
 * AR Session Management
 * Manages the state of an AR experience session
 * Implements EventEmitter pattern for state change notifications
 */

/**
 * Session states
 */
export const SessionState = {
  IDLE: 'IDLE',
  QR_SCANNING: 'QR_SCANNING',
  CAMERA_INIT: 'CAMERA_INIT',
  DETECTING: 'DETECTING',
  TRACKING: 'TRACKING',
  PAUSED: 'PAUSED',
  ERROR: 'ERROR',
};

/**
 * ARSession class
 * Manages the lifecycle and state of an AR experience session
 */
export class ARSession {
  constructor() {
    this.sessionId = null;
    this.posterId = null;
    this.state = SessionState.IDLE;
    this.startTime = null;
    this.endTime = null;
    this.listeners = new Map();

    // Performance metrics
    this.frameRate = 0;
    this.trackingAccuracy = 0;
    this.detectionAttempts = 0;
    this.trackingLostCount = 0;

    // Device info
    this.deviceInfo = null;
  }

  /**
   * Initialize session
   * @param {string} sessionId - Session ID from API
   * @param {string} posterId - Poster ID
   */
  init(sessionId, posterId) {
    this.sessionId = sessionId;
    this.posterId = posterId;
    this.startTime = new Date().toISOString();
    this.deviceInfo = this._collectDeviceInfo();

    this._emit('init', { sessionId, posterId });
  }

  /**
   * Get current state
   * @returns {string} - Current session state
   */
  getState() {
    return this.state;
  }

  /**
   * Set session state
   * @param {string} newState - New state from SessionState enum
   */
  setState(newState) {
    if (!Object.values(SessionState).includes(newState)) {
      console.error(`Invalid session state: ${newState}`);
      return;
    }

    const oldState = this.state;

    if (oldState === newState) {
      return; // No change
    }

    this.state = newState;
    this._emit('stateChange', { oldState, newState, timestamp: Date.now() });

    console.log(`Session state: ${oldState} → ${newState}`);
  }

  /**
   * Update performance metrics
   * @param {object} metrics - Performance metrics
   */
  updateMetrics(metrics) {
    if (metrics.frameRate !== undefined) {
      this.frameRate = metrics.frameRate;
    }
    if (metrics.trackingAccuracy !== undefined) {
      this.trackingAccuracy = metrics.trackingAccuracy;
    }
    if (metrics.detectionAttempts !== undefined) {
      this.detectionAttempts = metrics.detectionAttempts;
    }
    if (metrics.trackingLostCount !== undefined) {
      this.trackingLostCount = metrics.trackingLostCount;
    }

    this._emit('metricsUpdate', metrics);
  }

  /**
   * End session
   */
  end() {
    this.endTime = new Date().toISOString();
    this.setState(SessionState.IDLE);
    this._emit('end', { sessionId: this.sessionId, duration: this._getDuration() });
  }

  /**
   * Register event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Unregister event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function
   */
  off(event, callback) {
    if (!this.listeners.has(event)) {
      return;
    }

    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  /**
   * Get session summary
   * @returns {object} - Session summary data
   */
  getSummary() {
    return {
      sessionId: this.sessionId,
      posterId: this.posterId,
      state: this.state,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this._getDuration(),
      frameRate: this.frameRate,
      trackingAccuracy: this.trackingAccuracy,
      detectionAttempts: this.detectionAttempts,
      trackingLostCount: this.trackingLostCount,
      deviceInfo: this.deviceInfo,
    };
  }

  /**
   * Emit event to all listeners
   * @private
   */
  _emit(event, data) {
    if (!this.listeners.has(event)) {
      return;
    }

    const callbacks = this.listeners.get(event);
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for '${event}':`, error);
      }
    });
  }

  /**
   * Collect device information
   * @private
   */
  _collectDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: this._getPlatform(),
      browser: this._getBrowser(),
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
    };
  }

  /**
   * Detect platform (iOS/Android/Other)
   * @private
   */
  _getPlatform() {
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      return 'iOS';
    }
    if (/android/.test(ua)) {
      return 'Android';
    }
    return 'Other';
  }

  /**
   * Detect browser (Safari/Chrome/Other)
   * @private
   */
  _getBrowser() {
    const ua = navigator.userAgent.toLowerCase();
    if (/safari/.test(ua) && !/chrome/.test(ua)) {
      return 'Safari';
    }
    if (/chrome/.test(ua)) {
      return 'Chrome';
    }
    if (/firefox/.test(ua)) {
      return 'Firefox';
    }
    return 'Other';
  }

  /**
   * Calculate session duration in seconds
   * @private
   */
  _getDuration() {
    if (!this.startTime) {
      return 0;
    }

    const start = new Date(this.startTime).getTime();
    const end = this.endTime ? new Date(this.endTime).getTime() : Date.now();

    return Math.round((end - start) / 1000);
  }
}
